import {Input as HeadlessInput, Field, Label, Description} from "@headlessui/react";
import React, {forwardRef} from "preact/compat";
import classNames from "classnames";
import {Icon} from "@/elements-react/fix-icon";
import styles from "./Input.module.scss";
import {useState} from "preact/hooks";
import {useTranslation} from "react-i18next";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    name: string;
    className?: string;
    label?: string;
    helpText?: string;
    customError?: string;
    icon?: React.ReactNode;
    iconPosition?: "prefix" | "suffix";
    onIconClick?: () => void;
    clearable?: boolean;
    onClear?: () => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    inputSize?: "small" | "medium" | "large";
    suffix?: React.ReactNode;
    inputClassNames?: string;
    suffixClassNames?: string;
    showIsRequiredStar?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            name,
            label,
            helpText,
            customError,
            icon,
            iconPosition = "suffix",
            onIconClick,
            onChange,
            onInput,
            onClear,
            clearable = false,
            value,
            className,
            inputSize = "large",
            type = "text",
            suffix,
            inputClassNames,
            suffixClassNames,
            showIsRequiredStar = false,
            ...props
        },
        ref,
    ) => {
        const showClear = clearable && value;
        const helpMessage = customError || helpText;
        const hasSuffix = suffix || (icon && iconPosition === "suffix");
        const hasPrefix = icon && iconPosition === "prefix";

        const {t} = useTranslation();
        const [showPassword, setShowPassword] = useState(false);
        const isPassword = type === "password";
        const currentType = isPassword ? (showPassword ? "text" : "password") : type;
        const togglePassword = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            setShowPassword((prev) => !prev);
        }
        return (
            <Field className={classNames(styles.field, className)}>
                {label && <Label className={styles.label}>{label}</Label>}

                <div className={classNames(styles.wrapper, styles[inputSize])}>
                    {icon && iconPosition === "prefix" && (
                        <div className={classNames(styles.icon, styles.prefix)} onClick={onIconClick}>
                            {icon}
                        </div>
                    )}

                    <HeadlessInput
                        ref={ref}
                        name={name}
                        value={value}
                        invalid={!!customError}
                        onChange={onChange}
                        onInput={onInput}
                        type={currentType}
                        className={classNames(inputClassNames, styles.input, styles[inputSize], {
                            [styles.withPrefix]: hasPrefix,
                            [styles.withSuffix]: hasSuffix || showClear,
                        })}
                        {...(props as any)}
                    />

                    {showIsRequiredStar && !isPassword && !showClear && props.required && <span className={styles.required}>*</span>}

                    {showClear && !isPassword && !!customError && (
                        <div className={classNames(styles.icon, styles.clearIcon)} onClick={onClear}>
                            <Icon name="x-circle" width={20}/>
                        </div>
                    )}

                    {isPassword && (
                        <button
                            className={classNames(styles.suffix, styles.passwordToggle, {
                                [styles.icon]: icon && iconPosition === "suffix",
                                [styles.textSuffix]: suffix && !(icon && iconPosition === "suffix"),
                            })}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={togglePassword}
                            title={showPassword ? t("b2c.x9h2p4a8pw-hide", "Hide password") : t("b2c.k1r3u7t2pw-show", "Show password")}
                        >
                            <Icon name={showPassword ? "eye-off" : "eye"} width={20}/>
                        </button>
                    )}

                    {!showClear && hasSuffix && (
                        <div
                            className={classNames(styles.suffix, suffixClassNames, {
                                [styles.icon]: icon && iconPosition === "suffix",
                                [styles.textSuffix]: suffix && !(icon && iconPosition === "suffix"),
                            })}
                            onClick={icon && iconPosition === "suffix" ? onIconClick : undefined}
                        >
                            {suffix || icon}
                        </div>
                    )}
                </div>

                {helpMessage && (
                    <Description
                        className={classNames(styles.description, {
                            [styles.error]: !!customError,
                        })}
                    >
                        {helpMessage}
                    </Description>
                )}
            </Field>
        );
    },
);
